import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ExternalLink,
  Globe,
  Check,
  X as XIcon,
  Loader2,
  Pencil,
  CalendarCheck,
  LayoutTemplate,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const BASE_DOMAIN = "lokalnie.dev.sorto.ai";
const REQ = { withCredentials: true };

function SubdomainEditor({ business, onClose, onSaved }) {
  const [slug, setSlug] = useState(business.subdomain || "");
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!slug || slug === business.subdomain) {
      setCheck(null);
      return;
    }
    setChecking(true);
    const handle = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/my/subdomain-check`, {
          params: { slug },
          ...REQ,
        });
        setCheck(res.data);
      } catch (e) {
        setCheck({ available: false, reason: "Sprawdzenie nie udalo sie" });
      } finally {
        setChecking(false);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [slug, business.subdomain]);

  const canSave =
    slug &&
    slug !== business.subdomain &&
    check?.available === true &&
    !checking;

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API}/my/businesses/${business.business_id}/subdomain`,
        { subdomain: slug },
        REQ,
      );
      toast.success("Adres zmieniony. Pierwsze otwarcie nowej domeny moze potrwac 2-3 sekundy (wystawiamy certyfikat SSL).");
      onSaved(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nie udalo sie zmienic adresu");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Zmien adres strony</DialogTitle>
          <DialogDescription>
            Twoja strona dostepna bedzie pod adresem{" "}
            <code className="text-emerald-700">https://{slug || "..."}.{BASE_DOMAIN}</code>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="slug">Adres (subdomena)</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                id="slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                placeholder="moja-firma"
                className="font-mono"
                maxLength={40}
              />
              <span className="text-sm text-slate-500 whitespace-nowrap">.{BASE_DOMAIN}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Male litery, cyfry, mysliniki. 3-40 znakow.
            </p>
          </div>

          {slug && slug !== business.subdomain && (
            <div
              className={`flex items-start gap-2 text-sm p-2 rounded-md ${
                checking
                  ? "bg-slate-50 text-slate-600"
                  : check?.available
                  ? "bg-emerald-50 text-emerald-800"
                  : "bg-red-50 text-red-800"
              }`}
            >
              {checking ? (
                <Loader2 className="w-4 h-4 mt-0.5 animate-spin" />
              ) : check?.available ? (
                <Check className="w-4 h-4 mt-0.5" />
              ) : (
                <XIcon className="w-4 h-4 mt-0.5" />
              )}
              <span>
                {checking
                  ? "Sprawdzam dostepnosc..."
                  : check?.available
                  ? "Adres wolny — mozesz go uzyc"
                  : check?.reason || "Niedostepny"}
              </span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Anuluj
          </Button>
          <Button onClick={handleSave} disabled={!canSave || saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Zapisz zmiane
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CustomDomainEditor({ business, onClose, onSaved, onDomainPatched }) {
  const isPremium = business.subscription_tier === "premium";
  const [domain, setDomain] = useState(business.custom_domain || "");
  const [dnsInfo, setDnsInfo] = useState(
    business.custom_domain_status === "pending_dns"
      ? { note: `Dodaj rekord CNAME wskazujacy na ${BASE_DOMAIN} (lub A na IP serwera dla domen bez 'www') u swojego rejestratora domeny, a nastepnie kliknij "Sprawdz DNS".` }
      : null
  );
  const [status, setStatus] = useState(business.custom_domain_status || null);
  const [reason, setReason] = useState(null);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    setReason(null);
    try {
      const res = await axios.patch(
        `${API}/my/businesses/${business.business_id}/custom-domain`,
        { domain },
        REQ,
      );
      setStatus(res.data.custom_domain_status);
      setDnsInfo(res.data.dns_instructions || null);
      toast.success("Domena zapisana — teraz ustaw DNS u rejestratora i sprawdz.");
      onDomainPatched?.({ custom_domain: res.data.custom_domain, custom_domain_status: res.data.custom_domain_status });
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || e?.response?.data?.detail || "Nie udalo sie zapisac domeny");
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    setReason(null);
    try {
      const res = await axios.post(
        `${API}/my/businesses/${business.business_id}/custom-domain/verify`,
        {},
        REQ,
      );
      if (res.data.success) {
        setStatus("active");
        toast.success("Domena aktywna! Pierwsze otwarcie moze potrwac 2-3 sekundy (wystawiamy certyfikat SSL).");
        onSaved();
      } else {
        setReason(res.data.reason);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Weryfikacja nie powiodla sie");
    } finally {
      setVerifying(false);
    }
  };

  const handleUnlink = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/my/businesses/${business.business_id}/custom-domain`,
        { domain: "" },
        REQ,
      );
      toast.success("Odlaczono wlasna domene");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nie udalo sie odlaczyc");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Wlasna domena</DialogTitle>
          <DialogDescription>
            Podepnij swoja domene (np. twojafirma.pl) zamiast subdomeny lokalnie.pro. Funkcja dostepna w pakiecie Premium.
          </DialogDescription>
        </DialogHeader>

        {!isPremium ? (
          <div className="text-sm bg-amber-50 text-amber-800 p-3 rounded-md">
            Ta funkcja jest dostepna w pakiecie Premium. Obecny pakiet: {business.subscription_tier || "brak"}.
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="custom-domain">Domena</Label>
              <Input
                id="custom-domain"
                value={domain}
                onChange={(e) => setDomain(e.target.value.toLowerCase().trim())}
                placeholder="twojafirma.pl"
                className="font-mono mt-1"
                disabled={status === "active"}
              />
            </div>

            {status && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-500">Status:</span>
                <Badge variant={status === "active" ? "default" : "secondary"}>
                  {status === "active" ? "Aktywna" : "Oczekuje na DNS"}
                </Badge>
              </div>
            )}

            {dnsInfo && status !== "active" && (
              <div className="text-xs bg-slate-50 p-3 rounded-md space-y-1 text-slate-700">
                <p>{dnsInfo.note}</p>
              </div>
            )}

            {reason && (
              <div className="flex items-start gap-2 text-sm p-2 rounded-md bg-red-50 text-red-800">
                <XIcon className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{reason}</span>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {business.custom_domain && (
            <Button variant="ghost" onClick={handleUnlink} disabled={saving} className="text-red-600 hover:text-red-700">
              Odlacz
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={saving || verifying}>
            Zamknij
          </Button>
          {isPremium && status !== "active" && (
            <Button
              variant="secondary"
              onClick={handleVerify}
              disabled={saving || verifying || status !== "pending_dns"}
            >
              {verifying && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Sprawdz DNS
            </Button>
          )}
          {isPremium && status !== "active" && (
            <Button onClick={handleSave} disabled={!domain || domain === business.custom_domain || saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Zapisz domene
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SlotifyEditor({ business, onClose, onSaved }) {
  const [slug, setSlug] = useState(business.slotify_slug || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async (clear = false) => {
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API}/my/businesses/${business.business_id}/slotify`,
        { slotify_slug: clear ? "" : slug },
        REQ,
      );
      toast.success(clear ? "Odlaczono Slotify" : "Polaczono ze Slotify");
      onSaved(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nie udalo sie zapisac");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Polacz ze Slotify</DialogTitle>
          <DialogDescription>
            Slotify to silnik rezerwacji online (slotify.sorto.ai). Wpisz slug swojej
            organizacji w Slotify — widget pojawi sie na Twojej stronie w lokalnie.pro.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="slotify-slug">Slug Slotify</Label>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-slate-500 whitespace-nowrap font-mono">slotify.../widget/</span>
              <Input
                id="slotify-slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                placeholder="demo-groomer"
                className="font-mono"
                maxLength={50}
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Znajdziesz go w panelu Slotify > Ustawienia > Widget.
            </p>
          </div>
          {business.slotify_widget_url && (
            <a
              href={business.slotify_widget_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-xs text-emerald-700 hover:underline truncate"
            >
              Podglad: {business.slotify_widget_url}
            </a>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {business.slotify_slug && (
            <Button
              variant="ghost"
              onClick={() => handleSave(true)}
              disabled={saving}
              className="text-red-600 hover:text-red-700"
            >
              Odlacz
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Anuluj
          </Button>
          <Button onClick={() => handleSave(false)} disabled={!slug || saving}>
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Zapisz
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MySitesSection() {
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [linkingSlotify, setLinkingSlotify] = useState(null);
  const [managingDomain, setManagingDomain] = useState(null);

  const fetchSites = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/my/businesses`, REQ);
      setSites(res.data.items || []);
    } catch (e) {
      // Brak biznesow = pusty stan; 401 obsluguje wyzszy poziom.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 py-8">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">Wczytuje Twoje strony...</span>
      </div>
    );
  }

  if (sites.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-slate-500">
          <Globe className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="font-medium text-slate-700">Nie masz jeszcze opublikowanej strony</p>
          <p className="text-sm mt-1">
            Po oplaceniu abonamentu Twoja firma pojawi sie tutaj z adresem do udostepniania.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sites.map((site) => (
          <Card key={site.business_id} className="overflow-hidden">
            <div className="flex">
              {site.photo && (
                <div className="w-24 sm:w-32 bg-slate-200 shrink-0">
                  <img
                    src={site.photo}
                    alt={site.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="flex-1 p-4 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-slate-900 truncate">{site.name}</h3>
                    <p className="text-xs text-slate-500 truncate">
                      {site.category} · {site.city}
                    </p>
                  </div>
                  {site.subscription_tier && (
                    <Badge variant="secondary" className="text-xs shrink-0">
                      {site.subscription_tier}
                    </Badge>
                  )}
                </div>

                <div className="mt-3 flex items-center gap-1 text-xs">
                  <Globe className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  <a
                    href={site.public_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-700 hover:underline truncate font-mono"
                  >
                    {site.custom_domain_status === "active" ? site.custom_domain : `${site.subdomain}.${BASE_DOMAIN}`}
                  </a>
                  {site.custom_domain && site.custom_domain_status !== "active" && (
                    <Badge variant="outline" className="text-[10px] shrink-0">domena: oczekuje DNS</Badge>
                  )}
                </div>

                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(site.public_url, "_blank")}
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1" />
                    Otworz
                  </Button>
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => navigate(`/builder/${site.business_id}`)}
                  >
                    <LayoutTemplate className="w-3.5 h-3.5 mr-1" />
                    Edytuj strone
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditing(site)}
                  >
                    <Pencil className="w-3.5 h-3.5 mr-1" />
                    Zmien adres
                  </Button>
                  <Button
                    size="sm"
                    variant={site.slotify_slug ? "secondary" : "ghost"}
                    onClick={() => setLinkingSlotify(site)}
                    className={site.slotify_slug ? "" : "text-emerald-700"}
                  >
                    <CalendarCheck className="w-3.5 h-3.5 mr-1" />
                    {site.slotify_slug ? "Slotify polaczony" : "Polacz ze Slotify"}
                  </Button>
                  <Button
                    size="sm"
                    variant={site.custom_domain_status === "active" ? "secondary" : "ghost"}
                    onClick={() => setManagingDomain(site)}
                    className={site.custom_domain_status === "active" ? "" : "text-emerald-700"}
                  >
                    <Globe className="w-3.5 h-3.5 mr-1" />
                    {site.custom_domain_status === "active" ? "Wlasna domena" : "Podepnij wlasna domene"}
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {editing && (
        <SubdomainEditor
          business={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            fetchSites();
          }}
        />
      )}

      {linkingSlotify && (
        <SlotifyEditor
          business={linkingSlotify}
          onClose={() => setLinkingSlotify(null)}
          onSaved={() => {
            setLinkingSlotify(null);
            fetchSites();
          }}
        />
      )}

      {managingDomain && (
        <CustomDomainEditor
          business={managingDomain}
          onClose={() => setManagingDomain(null)}
          onSaved={() => {
            setManagingDomain(null);
            fetchSites();
          }}
          onDomainPatched={(patch) => {
            setSites((prev) =>
              prev.map((s) => (s.business_id === managingDomain.business_id ? { ...s, ...patch } : s))
            );
          }}
        />
      )}
    </>
  );
}
